import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { parseMoney2, roleAccess, type SupplierClaimDto } from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { usePurchaseOrders } from "@/api/hooks";
import { useApInvoices } from "@/api/gl";
import { apiFetch } from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { DocumentColumn as Column, DocumentRow as Row, RecentDocumentsPanel } from "@/components/domain/RecentDocuments";
import { formatMoney } from "@/domain/money";
import { translateStatus } from "@/domain/i18n";
import { GL_DOC_TONE, PO_STATUS_TONE, SUPPLIER_CLAIM_TONE } from "@/domain/status";
import { supplierDocumentAccess } from "./supplierDocumentAccess";

const SHOWN = 5;

/**
 * Chứng từ gần đây của một nhà cung cấp, trên trang chi tiết nhà cung cấp (18/09/2026).
 *
 * Anh Toàn đồng ý khi rà màn hình Nhà cung cấp: mở một nhà cung cấp là phải thấy ngay đang có đơn mua nào,
 * khiếu nại nào và hoá đơn nào của họ, như khối cùng tên bên trang khách hàng. Mỗi loại 5 cái mới nhất,
 * "Xem tất cả" mở danh sách đã lọc theo nhà cung cấp này (các trang đó đọc `?supplierId=`).
 *
 * Cột nào tài khoản không mở được thì không hiện, cũng không gọi API (`supplierDocumentAccess`).
 */
export function SupplierDocuments({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { po: mayPo, claims: mayClaims, ap: mayAp } = supplierDocumentAccess(user ? (user.access ?? roleAccess(user.role)) : null);

  const pos = usePurchaseOrders({ supplierId, page: 1, pageSize: SHOWN }, mayPo);
  const claims = useQuery({
    queryKey: ["supplier-claims", "supplier", supplierId],
    enabled: mayClaims,
    queryFn: ({ signal }) =>
      apiFetch<{ data: SupplierClaimDto[]; total: number }>("/supplier-claims", { query: { supplierId, page: 1 }, signal }),
  });
  const aps = useApInvoices("", 1, supplierId, mayAp);
  const q = encodeURIComponent(supplierId);

  const columns = [
    mayPo && (
      <Column key="po" title={t("nav.purchaseOrders")} total={pos.data?.total} loading={pos.isLoading} error={pos.error} allTo={`/purchase-orders?supplierId=${q}`}>
        {pos.data?.data.slice(0, SHOWN).map((r) => (
          // Đơn mua của một đợt tìm nguồn chỉ mở được ở khu tìm nguồn, như trên trang Đơn mua hàng.
          <Row key={r.id} to={r.sourcingCaseId ? `/sourcing/orders/${r.id}` : `/purchase-orders/${r.id}`} no={r.poNumber} date={r.orderDate} money={formatMoney(r.total, r.currency)}>
            <Badge tone={PO_STATUS_TONE[r.status] ?? "neutral"}>{translateStatus(t, r.status)}</Badge>
          </Row>
        ))}
      </Column>
    ),
    mayClaims && (
      <Column key="claims" title={t("nav.supplierClaims")} total={claims.data?.total} loading={claims.isLoading} error={claims.error} allTo={`/supplier-claims?supplierId=${q}`}>
        {claims.data?.data.slice(0, SHOWN).map((r) => (
          <Row key={r.id} to={`/supplier-claims/${r.id}`} no={r.claimNo} date={r.sentAt ?? r.createdAt} money={r.claimedMinor && r.currency ? formatMoney(r.claimedMinor, r.currency) : "—"}>
            <Badge tone={SUPPLIER_CLAIM_TONE[r.status] ?? "neutral"}>{t(`supplierClaims.statuses.${r.status}`)}</Badge>
          </Row>
        ))}
      </Column>
    ),
    mayAp && (
      <Column key="ap" title={t("gl.ap")} total={aps.data?.total} loading={aps.isLoading} error={aps.error} allTo={`/finance?tab=ap&supplierId=${q}`}>
        {aps.data?.data.slice(0, SHOWN).map((r) => (
          // Hoá đơn nhà cung cấp chưa có trang riêng: mở tab Hoá đơn nhà cung cấp ở Tài chính, tìm đúng số này.
          <Row key={r.id} to={`/finance?tab=ap&q=${encodeURIComponent(r.documentNo)}`} no={r.documentNo} date={r.invoiceDate} money={formatMoney(parseMoney2(r.totalAmount), r.currency)}>
            <span className="flex items-center gap-2">
              <span className="font-mono text-2xs text-ink-faint">{r.poNumber}</span>
              <Badge tone={GL_DOC_TONE[r.status] ?? "neutral"}>{t(`gl.${r.status}`)}</Badge>
            </span>
          </Row>
        ))}
      </Column>
    ),
  ].filter(Boolean);

  return <RecentDocumentsPanel title={t("suppliers.documents")} hint={t("suppliers.documentsHint")} columns={columns} />;
}
